import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ComponentsModule } from '../../../../components/components.module';
import { TwoFactorService } from '../../../../core/services/two-factor.service';
import { ConfirmationService } from '../../../../core/services/confirmation.service';
import { TwoFactorEnableData, TwoFactorSetupData } from '../../../../core/interfaces/two-factor';
import { mapTwoFactorError } from '../../../../core/utils/two-factor-error.utils';
import { notBlankValidator } from '../../../../core/validators/not-blank.validator';
import { RecoveryCodesListComponent } from '../recovery-codes-list/recovery-codes-list.component';

/** Steps of the add flow. 'recovery' only appears for the account's first authenticator. */
export type AddAuthenticatorStep = 'details' | 'scan' | 'recovery';

export const AUTHENTICATOR_LABEL_MAX_LENGTH = 100;
const TOTP_CODE_LENGTH = 6;
const EMPTY_DIGITS = (): string[] => Array<string>(TOTP_CODE_LENGTH).fill('');

/**
 * Modal that adds one authenticator to the login (HD-2013).
 *
 * Step 1 asks for a label and the password and only then calls generate.
 * Step 2 shows the QR code and takes the first code from the new phone.
 * Step 3 shows the recovery codes, but only when the backend returned
 * them, which it does for the first authenticator alone.
 *
 * The modal cannot be closed (backdrop, X, Escape) while generate or
 * enable is in flight, so a request never finishes against a closed modal.
 */
@Component({
  selector: 'app-two-factor-add-modal',
  templateUrl: './two-factor-add-modal.component.html',
  styleUrl: './two-factor-add-modal.component.scss',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, ComponentsModule, RecoveryCodesListComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorAddModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  /** True while 2FA is still off: the step indicator then shows the backup step. */
  @Input() isFirstAuthenticator = false;

  /** Emits true when the authenticator switched 2FA on (recovery codes were shown). */
  @Output() added = new EventEmitter<boolean>();
  /** The backend refused because this session never passed 2FA. */
  @Output() sessionRequired = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  step: AddAuthenticatorStep = 'details';

  // Step 1
  detailsForm!: FormGroup;
  detailsSubmitted = false;
  detailsError = '';
  isGenerating = false;

  // Step 2
  /** The pending setup that generate created; enable must name it. */
  pendingMethodId: number | null = null;
  qrCode: SafeHtml | null = null;
  secret = '';
  showManualSecret = false;
  verificationDigits: string[] = EMPTY_DIGITS();
  scanError = '';
  isVerifying = false;

  // Step 3
  recoveryCodes: string[] = [];
  codesAcknowledged = false;

  readonly labelMaxLength = AUTHENTICATOR_LABEL_MAX_LENGTH;

  constructor(
    private fb: FormBuilder,
    private twoFactorService: TwoFactorService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.detailsForm = this.fb.group({
      label: ['', [notBlankValidator(), Validators.maxLength(AUTHENTICATOR_LABEL_MAX_LENGTH)]],
      password: ['', [Validators.required]],
    });
  }

  /** While true the modal refuses to close: backdrop, X and Escape do nothing. */
  get isRequestPending(): boolean {
    return this.isGenerating || this.isVerifying;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.cancel();
  }

  // ---------------------------------------------------------------------------
  // Step 1: label + password
  // ---------------------------------------------------------------------------

  get labelError(): string {
    const control = this.detailsForm.get('label');
    if (!control?.errors || !(control.touched || this.detailsSubmitted)) {
      return '';
    }
    if (control.errors['required']) {
      return this.translate.instant('profile.authenticators.label_required');
    }
    return this.translate.instant('profile.authenticators.label_too_long', { max: AUTHENTICATOR_LABEL_MAX_LENGTH });
  }

  get passwordError(): string {
    const control = this.detailsForm.get('password');
    if (!control?.errors || !(control.touched || this.detailsSubmitted)) {
      return '';
    }
    return this.translate.instant('profile.password_required');
  }

  submitDetails(): void {
    this.detailsSubmitted = true;
    this.detailsError = '';
    if (this.detailsForm.invalid || this.isGenerating) {
      this.detailsForm.markAllAsTouched();
      return;
    }

    const { label, password } = this.detailsForm.value;
    this.isGenerating = true;

    this.twoFactorService.generate(label.trim(), password)
      .pipe(
        finalize(() => {
          this.isGenerating = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => this.onSetupGenerated(response.data),
        error: (error: unknown) => {
          this.detailsError = this.messageFor(error, 'profile.2fa_enable_failed');
          this.cdr.markForCheck();
        },
      });
  }

  private onSetupGenerated(data: TwoFactorSetupData | undefined): void {
    if (!data) {
      this.detailsError = this.translate.instant('profile.2fa_enable_failed');
      this.cdr.markForCheck();
      return;
    }
    this.pendingMethodId = data.method_id;
    this.qrCode = this.sanitizer.bypassSecurityTrustHtml(data.qr_code);
    this.secret = data.secret;
    this.goToScanStep();
  }

  private goToScanStep(): void {
    this.step = 'scan';
    this.scanError = '';
    this.showManualSecret = false;
    this.verificationDigits = EMPTY_DIGITS();
    this.cdr.markForCheck();
    this.focusFirstDigit();
  }

  // ---------------------------------------------------------------------------
  // Step 2: scan the QR code and confirm with the first code
  // ---------------------------------------------------------------------------

  toggleManualSecret(): void {
    this.showManualSecret = !this.showManualSecret;
  }

  copySecret(): void {
    if (!this.secret) {
      return;
    }
    navigator.clipboard.writeText(this.secret).then(
      () => this.confirmationService.swalSuccess(this.translate.instant('profile.secret_copied')),
      () => this.confirmationService.swalError(this.translate.instant('profile.authenticators.errors.copy_failed'))
    );
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const lastTypedCharacter = input.value.slice(-1);

    input.value = lastTypedCharacter;
    this.verificationDigits[index] = lastTypedCharacter;
    this.scanError = '';

    if (lastTypedCharacter && index < TOTP_CODE_LENGTH - 1) {
      this.focusDigit(index + 1);
    }
    if (this.isCodeComplete()) {
      this.verifyCode();
    }
  }

  onDigitKeyDown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.verificationDigits[index] && index > 0) {
      this.focusDigit(index - 1);
      event.preventDefault();
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      this.focusDigit(index - 1);
      event.preventDefault();
      return;
    }
    if (event.key === 'ArrowRight' && index < TOTP_CODE_LENGTH - 1) {
      this.focusDigit(index + 1);
      event.preventDefault();
      return;
    }

    const isDigit = /^\d$/.test(event.key);
    const isEditingKey = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key);
    if (!isDigit && !isEditingKey) {
      event.preventDefault();
    }
  }

  isCodeComplete(): boolean {
    return this.verificationDigits.every((digit) => digit.length === 1);
  }

  verifyCode(): void {
    if (!this.isCodeComplete() || this.isVerifying || this.pendingMethodId === null) {
      return;
    }

    this.isVerifying = true;
    this.scanError = '';

    this.twoFactorService.enable(this.pendingMethodId, this.verificationDigits.join(''))
      .pipe(
        finalize(() => {
          this.isVerifying = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => this.onAuthenticatorConfirmed(response.data),
        error: (error: unknown) => this.onVerifyFailed(error),
      });
  }

  private onAuthenticatorConfirmed(data: TwoFactorEnableData | undefined): void {
    const recoveryCodes = data?.recovery_codes ?? [];
    if (recoveryCodes.length === 0) {
      // Not the first authenticator: 2FA was already on, nothing more to show.
      this.added.emit(false);
      return;
    }
    this.recoveryCodes = recoveryCodes;
    this.codesAcknowledged = false;
    this.step = 'recovery';
    this.cdr.markForCheck();
  }

  private onVerifyFailed(error: unknown): void {
    const mapped = mapTwoFactorError(error, 'profile.verification_failed');
    if (mapped.kind === 'session_required') {
      this.sessionRequired.emit();
      return;
    }

    const message = this.translate.instant(mapped.translationKey);
    const setupIsGone = mapped.kind === 'setup_expired' || mapped.kind === 'setup_not_started';
    if (setupIsGone) {
      // The pending setup no longer exists on the backend (expired, or a
      // colleague's generate replaced it): only a new QR code can help.
      this.startAgain();
      this.detailsError = message;
      this.cdr.markForCheck();
      return;
    }

    this.scanError = message;
    this.verificationDigits = EMPTY_DIGITS();
    this.cdr.markForCheck();
    this.focusFirstDigit();
  }

  /** Back to step 1 with the label kept, so an expired setup is quick to redo. */
  private startAgain(): void {
    this.step = 'details';
    this.detailsSubmitted = false;
    this.detailsError = '';
    this.detailsForm.patchValue({ password: '' });
    this.pendingMethodId = null;
    this.qrCode = null;
    this.secret = '';
    this.scanError = '';
    this.verificationDigits = EMPTY_DIGITS();
  }

  // ---------------------------------------------------------------------------
  // Step 3: recovery codes (first authenticator only)
  // ---------------------------------------------------------------------------

  completeSetup(): void {
    if (!this.codesAcknowledged) {
      return;
    }
    this.added.emit(true);
  }

  /** The user asked to close (backdrop, X, Escape or the Cancel button). */
  cancel(): void {
    if (this.isRequestPending) {
      return;
    }
    // Once the recovery codes are on screen the authenticator already exists;
    // closing without acknowledging would hide the codes for good.
    if (this.step === 'recovery') {
      return;
    }
    this.closed.emit();
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private messageFor(error: unknown, fallbackKey: string): string {
    const mapped = mapTwoFactorError(error, fallbackKey);
    if (mapped.kind === 'session_required') {
      this.sessionRequired.emit();
      return '';
    }
    return this.translate.instant(mapped.translationKey);
  }

  private focusFirstDigit(): void {
    setTimeout(() => this.focusDigit(0));
  }

  private focusDigit(index: number): void {
    const input = document.getElementById(`setup-digit-${index}`) as HTMLInputElement | null;
    input?.focus();
  }
}
